import { EventId } from '../../../../../commonv3/types/events';
import { action, computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../../commonv3/api/RemoteResourceV3';
import { EventShowView } from '../../../../types/events';
import { EventApis } from '../../../../apis/events';
import { UserEventReservationApis } from '../../../../../mainv2/api/user';
import { EventReservationApis } from '../../../../apis/eventReservations';
import { translateErrorMessage } from '../../../../../commonv3/api/protocol';
import { createContext, useContext } from 'react';
import { EventWaitlistState } from './EventWaitlistState';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { TokenType } from '../../../../../commonv3/types/tokens';
import moment from 'moment-timezone';

type EventProgress = 'detail' | 'reserve' | 'complete' | 'error';

class EventPaneState {
  eventId: EventId | null = null;
  event: RemoteResourceV3<EventShowView> = RemoteResourceV3.loading();
  errorMessage: string | null = null;
  alreadyReserved = false;
  submitting = false;
  progress: EventProgress = 'detail';
  eventWaitlistState = new EventWaitlistState();

  constructor() {
    makeObservable(this, {
      eventId: observable,
      event: observable,
      errorMessage: observable,
      alreadyReserved: observable,
      submitting: observable,
      progress: observable,
      eventWaitlistState: observable,
      qualifiedToReserve: computed,
      qualifiedToWatchVideo: computed,
      setProgress: action.bound,
      setError: action.bound,
      submit: action.bound,
      clear: action.bound,
    });
  }

  async init(eventId: EventId) {
    this.eventId = eventId;
    this.errorMessage = null;
    Promise.all([this.reloadEvent(), await this.reloadReservationStatus(), await this.eventWaitlistState.init()]);
  }

  async reloadEvent() {
    if (this.eventId == null) return;
    const eventId = this.eventId;
    await updateRemoteResourceV3(
      this.event,
      (v) => (this.event = v),
      () => EventApis.find(eventId)
    );
  }

  async reloadReservationStatus() {
    const userReservations = await UserEventReservationApis.findAll();
    this.alreadyReserved =
      userReservations.find((r) => r.event_type === 'Event' && r.event_id === this.eventId) != null;
  }

  get qualifiedToReserve() {
    return !!GlobalStatesV3.currentUser.availableTokens.findAffordableToken(
      TokenType.EVENT,
      1,
      this.event.get().starts_at
    );
  }

  get qualifiedToWatchVideo() {
    return !!GlobalStatesV3.currentUser.availableTokens.findAffordableToken(TokenType.EVENT, 1, moment());
  }

  setProgress(progress: Exclude<EventProgress, 'error'>) {
    this.progress = progress;
  }

  setError(errorMessage: string) {
    this.progress = 'error';
    this.errorMessage = errorMessage;
  }

  async submit() {
    if (this.eventId == null) return;
    try {
      this.submitting = true;
      await EventReservationApis.create(this.eventId);
      this.progress = 'complete';
    } catch (e) {
      this.errorMessage = translateErrorMessage(e);
      this.progress = 'error';
    } finally {
      this.submitting = false;
    }
  }

  clear() {
    this.eventId = null;
    this.errorMessage = null;
    this.progress = 'detail';
  }
}

const ReservationPaneContext = createContext<{ value: EventPaneState | null }>({ value: null });

export const useEventReservationState = (eventId: number): EventPaneState => {
  const state = useContext(ReservationPaneContext);
  if (!state.value || state.value.eventId !== eventId) {
    state.value = new EventPaneState();
    state.value.init(eventId);
  }
  return state.value;
};
